/**
 * The app's one delete confirmation (task 864).
 *
 * Before this the app had four patterns for the same decision: an inline
 * two-step Confirm/Cancel swap on the older list pages and on Artifacts, the
 * shared `ConfirmDialog` on three surfaces, two bespoke inline modals
 * (Environments, Custom Domains), and no confirmation at all on two destructive
 * actions. This composes the `ConfirmDialog` task 863 widened, and every delete
 * in the app goes through it.
 *
 * It renders three states, and the state is the service's answer, never a
 * guess:
 *
 * 1. **Refused** — the dependents read came back with a non-empty `blocking`
 *    list, so a live AI Service would break. There is NO confirm button; the
 *    dialog names the services and says to repoint or delete them first. This
 *    is the same `409 IN_USE` payload the DELETE itself would answer with, read
 *    a moment earlier so the User never spends a click to be refused.
 * 2. **Dependents** — things point at this resource but the delete proceeds by
 *    detaching them. The dialog names them, because deleting a knowledge base
 *    three agents search is a different decision from deleting one nobody does.
 * 3. **Plain** — nothing points at it.
 *
 * All three may carry `info` (task 870): sentences the SERVICE wrote about
 * consequences the two lists cannot express. They render verbatim, in the
 * refusal as well as the confirm, and the app never interprets them.
 *
 * `entity` is optional: an entity with no `/dependents` route (an artifact, an
 * endpoint test, a generated interface) simply passes none, and the dialog
 * renders state 3 with whatever `body` the caller supplies.
 */
import type { ReactNode } from 'react';
import { ConfirmDialog } from './ConfirmDialog';
import { useDependents } from '../../hooks/useDependents';
import type { DependentsEntity, DependentEndpointRef } from '../../types/dependents';

export interface DeleteResourceDialogProps {
  /** "agent", "prompt", "knowledge base" — lower case, used in sentences. */
  entityLabel: string;
  /** The resource's own name, quoted in the title. */
  name: string;
  /** The resource id, for the dependents read. */
  id: string;
  /** The entity's dependents route, when it has one. */
  entity?: DependentsEntity;
  /** Extra sentences for the plain and dependents states — what leaves, what stays. */
  body?: ReactNode;
  /**
   * False when the delete writes no ledger row and so cannot be restored — an
   * AMS-owned custom domain, for instance. The Archive line is then omitted
   * rather than promising a restore that does not exist.
   */
  restorable?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isPending?: boolean;
  /** When set, the User must type this before Confirm enables. */
  confirmText?: string;
}

/** "the AI Services `a`, `b` and `c`" — an Oxford-free list, as a sentence reads. */
function serviceList(services: DependentEndpointRef[]): string {
  const names = services.map((service) => service.name || service.path);
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export function DeleteResourceDialog({
  entityLabel,
  name,
  id,
  entity,
  body,
  restorable = true,
  onClose,
  onConfirm,
  isPending = false,
  confirmText,
}: DeleteResourceDialogProps) {
  // Only entities with the route ask; the hook is called unconditionally (rules
  // of hooks) and disabled for the rest, which is why it takes an `enabled`.
  const { data, isLoading } = useDependents(entity ?? 'agents', id, !!entity);

  const blocking = data?.blocking ?? [];
  const isRefused = blocking.length > 0;

  const detaching = [
    ...(data?.dependents.agents ?? []).map((ref) => ({ ...ref, kind: 'agent' as const })),
    ...(data?.dependents.collaborationConfigs ?? []).map((ref) => ({
      ...ref,
      kind: 'collaboration config' as const,
    })),
    // AI Services reach this list only when they are NOT blocking — a service
    // deployed on a deployment environment right now is in `blocking` and the
    // dialog never gets here (task 870). What is left is history: services that
    // have deployed onto the lane before and would lose that address.
    ...(data?.dependents.endpoints ?? []).map((ref) => ({
      ...ref,
      kind: 'AI Service' as const,
    })),
  ];

  /**
   * The service's own sentences about what else the removal touches (task 870).
   * Rendered verbatim in BOTH states and never interpreted — a refusal is
   * exactly when a User most needs to know what the thing is carrying.
   */
  const info = data?.info ?? [];
  const infoNote = info.length > 0 && (
    <ul
      className="list-disc pl-5 space-y-1 text-gray-500 dark:text-gray-400"
      data-testid="delete-resource-info"
    >
      {info.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );

  if (isRefused) {
    return (
      <ConfirmDialog
        isOpen
        onClose={onClose}
        // Unreachable: the refusal state renders no confirm button. Passing a
        // no-op keeps the prop honest rather than pretending there is an action.
        onConfirm={() => undefined}
        confirmStyle="danger"
        title={`Cannot delete “${name}” yet`}
        hideConfirm
        cancelLabel="Close"
        message={
          <div className="space-y-3" data-testid="delete-resource-refused">
            <p>
              This {entityLabel} is used by the AI Service
              {blocking.length === 1 ? ' ' : 's '}
              <span className="font-semibold">{serviceList(blocking)}</span>, which would stop
              working.
            </p>
            <ul className="list-disc pl-5 space-y-1" data-testid="delete-resource-blocking">
              {blocking.map((service) => (
                <li key={service.id} data-testid={`delete-resource-blocking-${service.id}`}>
                  <span className="font-medium">{service.name || service.path}</span>{' '}
                  <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                    {service.path}
                  </span>
                </li>
              ))}
            </ul>
            <p>Repoint or delete {blocking.length === 1 ? 'it' : 'those'} first, then try again.</p>
            {infoNote}
          </div>
        }
      />
    );
  }

  return (
    <ConfirmDialog
      isOpen
      onClose={onClose}
      onConfirm={onConfirm}
      isLoading={isPending}
      confirmStyle="danger"
      confirmLabel={`Delete ${entityLabel}`}
      title={`Delete “${name}”?`}
      confirmText={confirmText}
      message={
        <div className="space-y-3" data-testid="delete-resource-body">
          {body}

          {entity && isLoading && (
            <p className="text-gray-500 dark:text-gray-400" data-testid="delete-resource-checking">
              Checking what uses this {entityLabel}…
            </p>
          )}

          {detaching.length > 0 && (
            <div data-testid="delete-resource-dependents">
              <p>
                {detaching.length === 1 ? 'One resource uses' : `${detaching.length} resources use`}{' '}
                this {entityLabel} and will stop:
              </p>
              <ul className="mt-1 list-disc pl-5 space-y-1">
                {detaching.map((ref) => (
                  <li key={`${ref.kind}-${ref.id}`} data-testid={`delete-resource-dependent-${ref.id}`}>
                    <span className="font-medium">{ref.name}</span>{' '}
                    <span className="text-gray-500 dark:text-gray-400">({ref.kind})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {infoNote}

          {restorable && (
            <p data-testid="delete-resource-archive">
              You can restore it from Settings → Archive at any time.
            </p>
          )}
        </div>
      }
    />
  );
}

export default DeleteResourceDialog;
